import { useApi } from '@api/ApiProvider'
import type { MagicPlace, MagicPlaceProposal, MagicPlaceStatus } from '@api/types'
import { usePanelQuery, useStaffSession } from '@features/panel/StaffSession'
import { useQuery } from '@tanstack/react-query'
import { MAGIC_PLACES_KEY, STAFF_MAGIC_KEY } from './useModeration'

// Un lugar mágico en el mapa: con su estado y, para el personal, la propuesta entera.
export type MapMagicPlace = MagicPlace & { status: MagicPlaceStatus; proposal: MagicPlaceProposal | null }

export const fromPublic = (place: MagicPlace): MapMagicPlace => ({ ...place, status: 'approved', proposal: null })
export const fromProposal = (proposal: MagicPlaceProposal): MapMagicPlace => ({
	id: proposal.id,
	name: proposal.name,
	photoUrl: proposal.photoUrl,
	location: proposal.location,
	status: proposal.status,
	proposal,
})

// Los que se pintan: todos menos los retirados, salvo el que se está mirando (el gestor lo abre
// con "Ver en el mapa" o acaba de retirarlo), para que vea dónde está.
export const visibleOnMap = (places: MapMagicPlace[], shownId: string | null): MapMagicPlace[] =>
	places.filter((place) => place.status !== 'retired' || place.id === shownId)

// Lugares mágicos del mapa: sin sesión, los aprobados; con sesión de gestor, todos (con su estado),
// para revisarlos allí. Son un complemento: si fallan, el mapa se ve igual sin ellos.
export const useMapMagicPlaces = (): MapMagicPlace[] => {
	const api = useApi()
	const { session } = useStaffSession()
	const publicPlaces = useQuery({
		queryKey: MAGIC_PLACES_KEY,
		queryFn: () => api.magicPlaces(),
		enabled: !session,
	})
	const staffPlaces = usePanelQuery([...STAFF_MAGIC_KEY.slice(1), 'on-map'], (token) => api.magicPlacesOnMap(token))
	if (session) return (staffPlaces.data ?? []).map(fromProposal)
	return (publicPlaces.data ?? []).map(fromPublic)
}
